/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  CircularProgress,
  FormControl,
  Grid,
  TextField,
} from "@mui/material";
import {
  Control,
  UseFormSetValue,
  UseFormWatch,
  FieldValues,
  Path,
  useController,
} from "react-hook-form";
import { useTranslation } from "react-i18next";

import LabelInput from "../../components/input/LabelInput";
import {
  getLocationCities,
  getDistrictsByCity,
  getSubdistrictsByDistrict,
  getSubdistrictsByPostalCode,
  getLocationBySubdistrict,
} from "../../service/location.service";
import {
  City,
  District,
  Subdistrict,
  FullLocation,
} from "../../models/location.types";
import { POSTAL_CODES } from "../../constants/unique_postal_codes";
import Grid2 from "@mui/material/Unstable_Grid2";

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

interface AutoLocationThailandProps<T extends FieldValues = FieldValues> {
  control: Control<T>;
  setValue: UseFormSetValue<T>;
  watch: UseFormWatch<T>;
  prefix?: string;
  disabled?: boolean;
  initialSubdistrictRefId?: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

const fp = <T extends FieldValues>(prefix: string, field: string) =>
  `${prefix}${field}` as Path<T>;

function uniqueById<T extends { id: string }>(arr: T[]): T[] {
  const seen = new Set<string>();
  return arr.filter((item) => {
    if (seen.has(item.id)) return false;
    seen.add(item.id);
    return true;
  });
}

const sv = <T extends FieldValues>(
  setValue: UseFormSetValue<T>,
  field: Path<T>,
  value: any,
  dirty = true,
) => setValue(field, value, { shouldDirty: dirty, shouldValidate: true });

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────

export default function AutoLocationThailand<
  T extends FieldValues = FieldValues,
>({
  control,
  setValue,
  watch,
  prefix = "",
  disabled = false,
  initialSubdistrictRefId,
}: AutoLocationThailandProps<T>) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.includes("th") ? "th" : "en";

  // ── State ─────────────────────────────────────────────────
  const [cities, setCities]                   = useState<City[]>([]);
  const [apiDistricts, setApiDistricts]       = useState<District[]>([]);
  const [apiSubdistricts, setApiSubdistricts] = useState<Subdistrict[]>([]);
  const [postalCodeResults, setPostalCodeResults] = useState<FullLocation[]>([]);
  const [isPostalCodeFlow, setIsPostalCodeFlow]   = useState(false);
  const [isHydrating, setIsHydrating]             = useState(!!initialSubdistrictRefId);

  const [loadingCities, setLoadingCities]             = useState(false);
  const [loadingDistricts, setLoadingDistricts]       = useState(false);
  const [loadingSubdistricts, setLoadingSubdistricts] = useState(false);
  const [loadingPostalCode, setLoadingPostalCode]     = useState(false);
  const [loadingHydrate, setLoadingHydrate]           = useState(false);

  // ── Watch ─────────────────────────────────────────────────
  const postalCode      = watch(fp<T>(prefix, "postalCode"));
  const cityRefId       = watch(fp<T>(prefix, "cityRefId"));
  const districtRefId   = watch(fp<T>(prefix, "districtRefId"));
  const subdistrictRefId = watch(fp<T>(prefix, "subdistrictRefId")); // eslint-disable-line @typescript-eslint/no-unused-vars

  const postalCodeOptions = useMemo(() => POSTAL_CODES as string[], []);

  const locName = useCallback(
    (name?: { en: string; th: string }) =>
      name ? (lang === "th" ? name.th : name.en) : "",
    [lang],
  );

  // ── Computed options ──────────────────────────────────────
  // postal code flow: options มาจาก postalCodeResults โดยตรง
  // city flow:        options มาจาก API ที่โหลดมาทีละ step

  const districtOptions: District[] = useMemo(() => {
    if (isPostalCodeFlow && postalCodeResults.length > 0) {
      return uniqueById(postalCodeResults.map((r) => r.district));
    }
    return apiDistricts;
  }, [isPostalCodeFlow, postalCodeResults, apiDistricts]);

  const subdistrictOptions: Subdistrict[] = useMemo(() => {
    if (isPostalCodeFlow && postalCodeResults.length > 0) {
      const filtered = districtRefId
        ? postalCodeResults
            .filter((r) => r.district.id === districtRefId)
            .map((r) => r.subdistrict)
        : postalCodeResults.map((r) => r.subdistrict);
      return uniqueById(filtered);
    }
    return apiSubdistricts;
  }, [isPostalCodeFlow, postalCodeResults, districtRefId, apiSubdistricts]);

  const selectedCity        = cities.find((c) => c.id === cityRefId) ?? null;
  const selectedDistrict    = districtOptions.find((d) => d.id === districtRefId) ?? null;
  const selectedSubdistrict = subdistrictOptions.find((s) => s.id === subdistrictRefId) ?? null;

  // ── Effect: โหลด cities ครั้งแรก ─────────────────────────
  // service return City[] โดยตรง ไม่ต้อง unwrap
  useEffect(() => {
    let cancelled = false;
    setLoadingCities(true);
    getLocationCities()
      .then((data) => { if (!cancelled) setCities(data); })
      .finally(() => { if (!cancelled) setLoadingCities(false); });
    return () => { cancelled = true; };
  }, []);

  // ── Effect: Hydrate จาก initialSubdistrictRefId (edit mode) ──
  // getLocationBySubdistrict() return FullLocation โดยตรง (service unwrap แล้ว)
  useEffect(() => {
    if (!initialSubdistrictRefId) return;

    let cancelled = false;
    setIsHydrating(true);
    setLoadingHydrate(true);

    getLocationBySubdistrict(initialSubdistrictRefId)
      .then(async (ref) => {
        
        if (cancelled) return;

        // ref = FullLocation โดยตรง มี city, district, subdistrict, postal_code
        sv(setValue, fp<T>(prefix, "postalCode"),        ref.postal_code,       false);
        sv(setValue, fp<T>(prefix, "cityRefId"),         ref.city.id,           false);
        sv(setValue, fp<T>(prefix, "districtRefId"),     ref.district.id,       false);
        sv(setValue, fp<T>(prefix, "subdistrictRefId"),  ref.subdistrict.id,    false);

        const [districts, subdistricts] = await Promise.all([
          getDistrictsByCity(ref.city.id),
          getSubdistrictsByDistrict(ref.district.id),
        ]);

        if (cancelled) return;
        setApiDistricts(districts);
        setApiSubdistricts(subdistricts);
      })
      .catch((err) => { console.error("AutoLocationThailand hydrate failed:", err); })
      .finally(() => {
        if (!cancelled) {
          setIsHydrating(false);
          setLoadingHydrate(false);
        }
      });

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialSubdistrictRefId]);

  // ── Effect: Flow A – Postal Code ─────────────────────────
  // getSubdistrictsByPostalCode() return FullLocation[] โดยตรง
  useEffect(() => {
    if (isHydrating) return;
    if (!postalCode || postalCode.length < 5) {
      setPostalCodeResults([]);
      return;
    }
    if (!isPostalCodeFlow) return;

    let cancelled = false;
    setLoadingPostalCode(true);

    getSubdistrictsByPostalCode(postalCode)
      .then((refs) => {
        if (cancelled) return;
        setPostalCodeResults(refs);
        if (refs.length === 0) return;

        const uniqueCities = uniqueById(refs.map((r) => r.city));
        if (uniqueCities.length === 1) {
          sv(setValue, fp<T>(prefix, "cityRefId"), refs[0].city.id);
        }

        const uniqueDistricts = uniqueById(refs.map((r) => r.district));
        if (uniqueDistricts.length === 1) {
          sv(setValue, fp<T>(prefix, "districtRefId"), refs[0].district.id);
          if (refs.length === 1) {
            sv(setValue, fp<T>(prefix, "subdistrictRefId"), refs[0].subdistrict.id);
          }
        } else {
          sv(setValue, fp<T>(prefix, "districtRefId"), "");
          sv(setValue, fp<T>(prefix, "subdistrictRefId"), "");
        }
      })
      .catch(() => { if (!cancelled) setPostalCodeResults([]); })
      .finally(() => { if (!cancelled) setLoadingPostalCode(false); });

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postalCode, isPostalCodeFlow]);

  // ── Effect: Flow B – City → Districts ────────────────────
  // getDistrictsByCity() return District[] โดยตรง
  useEffect(() => {
    if (isHydrating) return;
    if (isPostalCodeFlow) return;
    if (!cityRefId) { setApiDistricts([]); return; }

    let cancelled = false;
    setLoadingDistricts(true);
    getDistrictsByCity(cityRefId)
      .then((data) => { if (!cancelled) setApiDistricts(data); })
      .finally(() => { if (!cancelled) setLoadingDistricts(false); });
    return () => { cancelled = true; };
  }, [cityRefId, isPostalCodeFlow, isHydrating]);

  // ── Effect: Flow B – District → Subdistricts ─────────────
  // getSubdistrictsByDistrict() return Subdistrict[] โดยตรง
  useEffect(() => {
    if (isHydrating) return;
    if (isPostalCodeFlow) return;
    if (!districtRefId) { setApiSubdistricts([]); return; }

    let cancelled = false;
    setLoadingSubdistricts(true);
    getSubdistrictsByDistrict(districtRefId)
      .then((data) => { if (!cancelled) setApiSubdistricts(data); })
      .finally(() => { if (!cancelled) setLoadingSubdistricts(false); });
    return () => { cancelled = true; };
  }, [districtRefId, isPostalCodeFlow, isHydrating]);

  // ── Reset helpers ─────────────────────────────────────────
  const resetDownstream = useCallback(
    (from: "postalCode" | "city" | "district") => {
      if (from === "postalCode") {
        sv(setValue, fp<T>(prefix, "cityRefId"),        "");
        sv(setValue, fp<T>(prefix, "districtRefId"),    "");
        sv(setValue, fp<T>(prefix, "subdistrictRefId"), "");
        setApiDistricts([]);
        setApiSubdistricts([]);
        setPostalCodeResults([]);
      }
      if (from === "city") {
        sv(setValue, fp<T>(prefix, "districtRefId"),    "");
        sv(setValue, fp<T>(prefix, "subdistrictRefId"), "");
        if (!isPostalCodeFlow) sv(setValue, fp<T>(prefix, "postalCode"), "");
        setApiDistricts([]);
        setApiSubdistricts([]);
      }
      if (from === "district") {
        sv(setValue, fp<T>(prefix, "subdistrictRefId"), "");
        if (!isPostalCodeFlow) sv(setValue, fp<T>(prefix, "postalCode"), "");
        setApiSubdistricts([]);
      }
    },
    [prefix, setValue, isPostalCodeFlow],
  );

  // ── Register hidden fields กับ RHF ──────────────────────
  useController({ name: fp<T>(prefix, "cityRefId"),        control, rules: { required: true } });
  useController({ name: fp<T>(prefix, "districtRefId"),    control, rules: { required: true } });
  useController({ name: fp<T>(prefix, "subdistrictRefId"), control, rules: { required: true } });

  // ─────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────
  return (
    <>
      {/* ── Postal Code ──────────────────────────────────── */}
      <Grid2 xs={12} md={6}>
        <FormControl fullWidth>
          <LabelInput
            label={t("location.postalCode", { defaultValue: lang === "th" ? "รหัสไปรษณีย์" : "Postal Code" })}
            required
          />
          <Autocomplete
            size="small"
            disabled={disabled || loadingHydrate}
            options={postalCodeOptions}
            value={postalCode || null}
            onChange={(_, newVal) => {
              setIsPostalCodeFlow(true);
              resetDownstream("postalCode");
              sv(setValue, fp<T>(prefix, "postalCode"), newVal ?? "");
            }}
            loading={loadingPostalCode}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={t("location.postalCodePlaceholder", { defaultValue: lang === "th" ? "เลือกรหัสไปรษณีย์" : "Select postal code" })}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingPostalCode && <CircularProgress color="inherit" size={18} />}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
                sx={{ zIndex: 0 }}
              />
            )}
            sx={{ backgroundColor: "var(--white)" }}
          />
        </FormControl>
      </Grid2>

      {/* ── City / Province ───────────────────────────────── */}
      <Grid2 xs={12} md={6}>
        <FormControl fullWidth>
          <LabelInput
            label={t("location.city", { defaultValue: lang === "th" ? "จังหวัด" : "Province" })}
            required
          />
          <Autocomplete
            size="small"
            disabled={disabled || loadingCities || loadingPostalCode || loadingHydrate}
            options={cities}
            value={selectedCity}
            onChange={(_, newVal) => {
              setIsPostalCodeFlow(false);
              setPostalCodeResults([]);
              resetDownstream("city");
              sv(setValue, fp<T>(prefix, "cityRefId"),  newVal?.id ?? "");
              sv(setValue, fp<T>(prefix, "postalCode"), "");
            }}
            readOnly={isPostalCodeFlow && !!cityRefId}
            loading={loadingCities}
            getOptionLabel={(o) => locName(o.name)}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={t("location.cityPlaceholder", { defaultValue: lang === "th" ? "เลือกจังหวัด" : "Select province" })}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingCities && <CircularProgress color="inherit" size={18} />}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
                sx={{ zIndex: 0 }}
              />
            )}
            sx={{ backgroundColor: "var(--white)" }}
          />
        </FormControl>
      </Grid2>

      {/* ── District ──────────────────────────────────────── */}
      <Grid2 xs={12} md={6}>
        <FormControl fullWidth>
          <LabelInput
            label={t("location.district", { defaultValue: lang === "th" ? "อำเภอ / เขต" : "District" })}
            required
          />
          <Autocomplete
            size="small"
            disabled={disabled || !cityRefId || loadingDistricts || loadingPostalCode || loadingHydrate}
            options={districtOptions}
            value={selectedDistrict}
            onChange={(_, newVal) => {
              resetDownstream("district");
              sv(setValue, fp<T>(prefix, "districtRefId"), newVal?.id ?? "");

              if (isPostalCodeFlow && newVal) {
                const matchedSubs = postalCodeResults.filter(
                  (r) => r.district.id === newVal.id,
                );
                if (matchedSubs.length === 1) {
                  sv(setValue, fp<T>(prefix, "subdistrictRefId"), matchedSubs[0].subdistrict.id);
                }
              }
            }}
            loading={loadingDistricts || loadingPostalCode}
            getOptionLabel={(o) => locName(o.name)}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={t("location.districtPlaceholder", { defaultValue: lang === "th" ? "เลือกอำเภอ / เขต" : "Select district" })}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {(loadingDistricts || loadingPostalCode) && <CircularProgress color="inherit" size={18} />}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
                sx={{ zIndex: 0 }}
              />
            )}
            sx={{ backgroundColor: "var(--white)" }}
          />
        </FormControl>
      </Grid2>

      {/* ── Sub-district ──────────────────────────────────── */}
      <Grid2 xs={12} md={6}>
        <FormControl fullWidth>
          <LabelInput
            label={t("location.subdistrict", { defaultValue: lang === "th" ? "ตำบล / แขวง" : "Sub-district" })}
            required
          />
          <Autocomplete
            size="small"
            disabled={disabled || !districtRefId || loadingSubdistricts || loadingPostalCode || loadingHydrate}
            options={subdistrictOptions}
            value={selectedSubdistrict}
            onChange={(_, newVal) => {
              sv(setValue, fp<T>(prefix, "subdistrictRefId"), newVal?.id ?? "");
              // city flow: ดึง postal_code จาก Subdistrict โดยตรง
              if (!isPostalCodeFlow && newVal?.postal_code) {
                sv(setValue, fp<T>(prefix, "postalCode"), newVal.postal_code);
              }
            }}
            loading={loadingSubdistricts || loadingPostalCode}
            getOptionLabel={(o) => locName(o.name)}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={t("location.subdistrictPlaceholder", { defaultValue: lang === "th" ? "เลือกตำบล / แขวง" : "Select sub-district" })}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {(loadingSubdistricts || loadingPostalCode) && <CircularProgress color="inherit" size={18} />}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
                sx={{ zIndex: 0 }}
              />
            )}
            sx={{ backgroundColor: "var(--white)" }}
          />
        </FormControl>
      </Grid2>
    </>
  );
}